import {
  LLMUsageCheckResultStatus,
  type TCheckResult,
} from '@conversed/rest/src/contracts/llm-usages/check-results';
import {
  DEFAULT_IS_PRIORITY_KNOWLEDGE,
  DEFAULT_REPORT_ISSUE,
  DEFAULT_REPORT_ISSUE_PARAMS,
  DEFAULT_SAVE_ANSWER_AS_TEMPLATE,
  DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA,
  type TConversationDetailsSchema,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import type {
  TDraftState,
  TTranslationState,
} from './conversation-review-modal.type';

export const EMPTY_DRAFT: TDraftState = {
  status: '',
  correctedAnswer: '',
  correctedURL: '',
  reportIssue: DEFAULT_REPORT_ISSUE,
  reportIssueParams: DEFAULT_REPORT_ISSUE_PARAMS,
  useForAIKnowledgeData: DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA,
  isPriorityKnowledge: DEFAULT_IS_PRIORITY_KNOWLEDGE,
  saveAnswerAsTemplate: DEFAULT_SAVE_ANSWER_AS_TEMPLATE,
  templateName: '',
};

// Builds a draft pre-populated with the persisted checkResult so that touching a single
// control on an already-reviewed card does not wipe the other saved fields (the alternative
// - falling back to EMPTY_DRAFT - would replace saved reportIssue/correctedAnswer/etc.
// with defaults the moment the user toggles any unrelated control).
export const buildDraftFromCheckResult = (
  checkResult: Omit<TCheckResult, 'id'> | undefined,
): TDraftState => {
  if (!checkResult?.status) {
    return EMPTY_DRAFT;
  }
  return {
    status: checkResult.status,
    correctedAnswer: checkResult.correctedAnswer ?? '',
    correctedURL: checkResult.correctedURL ?? '',
    reportIssue: checkResult.reportIssue ?? DEFAULT_REPORT_ISSUE,
    reportIssueParams:
      checkResult.reportIssueParams ?? DEFAULT_REPORT_ISSUE_PARAMS,
    useForAIKnowledgeData:
      checkResult.useForAIKnowledgeData ?? DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA,
    isPriorityKnowledge:
      checkResult.isPriorityKnowledge ?? DEFAULT_IS_PRIORITY_KNOWLEDGE,
    saveAnswerAsTemplate: DEFAULT_SAVE_ANSWER_AS_TEMPLATE,
    templateName: '',
  };
};

export const getDisplayedQuestion = (
  conversation: TConversationDetailsSchema | undefined,
  translationState: TTranslationState | null,
  showTranslated: boolean,
): string => {
  if (!conversation) {
    return '';
  }
  if (showTranslated && translationState) {
    return translationState.translatedQuestion;
  }
  return conversation.question;
};

export const getDisplayedAnswer = (
  conversation: TConversationDetailsSchema | undefined,
  translationState: TTranslationState | null,
  showTranslated: boolean,
): string => {
  if (!conversation) {
    return '';
  }
  if (showTranslated && translationState) {
    return translationState.translatedAnswer;
  }
  return conversation.answer || '';
};

// Compares a draft against the saved checkResult to detect pending changes that would
// be lost on navigation. Returns false when no draft entry exists (user never touched
// this card).
export const computeHasUnsavedChanges = (
  draft: TDraftState | undefined,
  checkResult: Omit<TCheckResult, 'id'> | undefined,
): boolean => {
  if (!draft) {
    return false;
  }

  if (draft.pendingHotTopicId !== undefined) {
    return true;
  }

  if (draft.status !== (checkResult?.status ?? '')) {
    return true;
  }

  if (
    draft.useForAIKnowledgeData !==
    (checkResult?.useForAIKnowledgeData ?? DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA)
  ) {
    return true;
  }

  // Priority Knowledge is status-independent, so compare it regardless of status.
  if (
    draft.isPriorityKnowledge !==
    (checkResult?.isPriorityKnowledge ?? DEFAULT_IS_PRIORITY_KNOWLEDGE)
  ) {
    return true;
  }

  if (draft.status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT) {
    if (draft.correctedAnswer !== (checkResult?.correctedAnswer ?? '')) {
      return true;
    }
    if (draft.correctedURL !== (checkResult?.correctedURL ?? '')) {
      return true;
    }
    if (
      draft.reportIssue !== (checkResult?.reportIssue ?? DEFAULT_REPORT_ISSUE)
    ) {
      return true;
    }
    // reportIssueParams only matters when the toggle is on
    if (draft.reportIssue) {
      const savedParams =
        checkResult?.reportIssueParams ?? DEFAULT_REPORT_ISSUE_PARAMS;
      if (
        JSON.stringify(draft.reportIssueParams) !== JSON.stringify(savedParams)
      ) {
        return true;
      }
    }
    // saveAnswerAsTemplate is a client-only one-shot toggle - not stored in checkResult
    if (draft.saveAnswerAsTemplate !== DEFAULT_SAVE_ANSWER_AS_TEMPLATE) {
      return true;
    }
  }

  return false;
};
